import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Storefront, ArrowRight, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (!r.ok) setErr(r.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#FFFFFF]">
      {/* Left visual */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1768678218064-72683d81bf2b?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Phone repair neon"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(26,31,28,0.82) 0%, rgba(36,71,55,0.72) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Storefront size={20} weight="duotone" />
            </div>
            <div className="font-display font-semibold tracking-tight text-lg">iRepair<span className="text-[#1E5FEB]">.</span></div>
          </div>
          <div>
            <div className="overline text-white/50 mb-4">POS · repair control</div>
            <h1 className="font-display font-semibold text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-xl">
              Run tickets.<br />Sell accessories.<br /><span className="text-[#1E5FEB]">Close shop clean.</span>
            </h1>
            <p className="mt-7 text-[15px] text-white/70 max-w-md leading-relaxed">
              A fast counter terminal for repair shops — intake devices, manage inventory, ring sales, print receipts and Dymo labels.
            </p>
          </div>
          <div className="font-mono text-[11px] text-white/35 tracking-[0.15em]">
            v1.0 · BUILT FOR THE COUNTER
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="col-span-2 flex items-center justify-center p-8 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline mb-3">Sign in</div>
          <h2 className="font-display font-semibold text-[40px] tracking-tight leading-none mb-8">Counter terminal</h2>

          <label className="block mb-5">
            <div className="text-[12px] text-[#4B5563] font-medium mb-2">Email</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              className="w-full h-14 px-4 bg-white border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 text-[15px] rounded-2xl transition-all"
            />
          </label>

          <label className="block mb-7">
            <div className="text-[12px] text-[#4B5563] font-medium mb-2">Password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              className="w-full h-14 px-4 bg-white border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 text-[15px] rounded-2xl transition-all"
            />
          </label>

          {err && (
            <div data-testid="login-error" className="mb-4 px-4 py-3 rounded-xl bg-[#DBEAFE] text-[#DC2626] text-sm font-medium">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full h-14 rounded-2xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[15px] font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
            style={{ boxShadow: "0 12px 32px rgba(36,71,55,0.22)" }}
          >
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} weight="bold" />
          </button>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              data-testid="forgot-password-link"
              className="text-[12px] text-[#4B5563] hover:text-[#1E5FEB] font-medium transition-colors"
            >
              Forgot password?
            </button>
          </div>
        </form>
      </div>
      <ForgotPasswordModal open={forgotOpen} defaultEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}

function ForgotPasswordModal({ open, defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-2xl border border-[#E5E7EB]" data-testid="forgot-password-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="overline mb-1">FORGOT PASSWORD</div>
            <h2 className="font-display font-semibold text-[20px] tracking-tight">Reset your password</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F8FAFC] rounded-full"><X size={18} weight="bold" /></button>
        </div>
        {sent ? (
          <div className="p-6 space-y-3 text-center" data-testid="forgot-sent">
            <div className="font-display text-[18px] font-semibold">Check your inbox</div>
            <p className="text-[13px] text-[#4B5563] leading-relaxed">
              If <span className="font-mono">{email}</span> matches an account on this POS, you'll receive a reset link in the next minute. The link expires in 1 hour.
            </p>
            <button onClick={onClose} className="mt-4 h-11 px-6 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold transition-colors">CLOSE</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-[12px] text-[#4B5563] leading-relaxed">
              Enter the email tied to your account. We'll send a reset link to it. If you don't receive it within a minute, ask an admin on the shop floor to reset it for you from Settings → Users.
            </p>
            <label className="block">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1.5">EMAIL</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="forgot-email"
                className="w-full h-11 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-[14px]"
              />
            </label>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-[#E5E7EB] text-[12px] font-semibold">CANCEL</button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="forgot-submit"
                className="flex-1 h-11 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] disabled:opacity-60 text-white text-[12px] font-semibold transition-colors"
              >
                {submitting ? "SENDING…" : "SEND RESET LINK"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
